import React, { useState, useEffect } from 'react';
import { listenToUnifiedCollection } from '../config/firebase';
import TeacherExamPinManager from './TeacherExamPinManager';
import PrintableExamGenerator from './PrintableExamGenerator';
import WorksheetRepository from './WorksheetRepository';
import { INSTITUTIONAL_STUDENTS } from '../data/institutionalRoster';

export default function TeacherDashboard() {
  const [selectedGroup, setSelectedGroup] = useState('all');
  const [activeDashboardTab, setActiveDashboardTab] = useState('activities'); // 'activities', 'students', 'exam_pin'
  const [activitiesList, setActivitiesList] = useState([]);
  const [studentsList, setStudentsList] = useState([]);
  const [lastSyncTime, setLastSyncTime] = useState('Cargando...');
  
  // 📺 Estado para activar/desactivar el Modo Proyector de Salón
  const [showProjectorMode, setShowProjectorMode] = useState(false);

  // Escuchadores en Tiempo Real de Firebase Cloud (Sincronización Unificada)
  useEffect(() => {
    // 1. Escuchador Unificado de Actividades y Notas en Tiempo Real
    const unsubActivities = listenToUnifiedCollection("activities", (cloudActivities) => {
      cloudActivities.sort((a, b) => {
        const timeA = a.timestamp ? new Date(a.timestamp).getTime() : 0;
        const timeB = b.timestamp ? new Date(b.timestamp).getTime() : 0;
        return timeB - timeA;
      });

      const demoActivities = [
        { id: 'EOH-601-1001', name: 'Ana María Gómez', group: 'Grupo 601', topic: 'Unidad 1 (Grado 6°)', type: 'Taller 20 Ejercicios', score: '18 / 20', pct: 90, xp: 180, date: '09/08/2026' },
        { id: 'EOH-601-1002', name: 'Carlos Eduardo Pérez', group: 'Grupo 601', topic: 'Unidad 1 (Grado 6°)', type: 'Examen ICFES', score: '8 / 10', pct: 80, xp: 160, date: '09/08/2026' },
        { id: 'EOH-601-1003', name: 'David Alejandro Martínez', group: 'Grupo 601', topic: 'Unidad 2 (Grado 6°)', type: 'Taller 20 Ejercicios', score: '20 / 20', pct: 100, xp: 200, date: '08/08/2026' },
        { id: 'EOH-601-1004', name: 'Sofía Valentina López', group: 'Grupo 601', topic: 'Unidad 1 (Grado 6°)', type: 'Taller 20 Ejercicios', score: '16 / 20', pct: 80, xp: 160, date: '08/08/2026' },
        { id: 'EOH-701-1031', name: 'Mateo Restrepo', group: 'Grupo 701', topic: 'Unidad 1 (Grado 7°)', type: 'Examen ICFES', score: '9 / 10', pct: 90, xp: 180, date: '07/08/2026' }
      ];

      const localActivities = JSON.parse(localStorage.getItem('motor_pedagogico_activities') || '[]');
      
      const allActivities = [...cloudActivities, ...localActivities, ...demoActivities];
      const uniqueActivitiesMap = new Map();
      allActivities.forEach(item => {
        const key = (item.id || '') + (item.topic || '') + (item.date || '') + (item.score || '');
        if (!uniqueActivitiesMap.has(key)) {
          uniqueActivitiesMap.set(key, item);
        }
      });

      setActivitiesList(Array.from(uniqueActivitiesMap.values()));
      setLastSyncTime(new Date().toLocaleTimeString('es-CO'));
    });

    // 2. Escuchador Unificado de Estudiantes Registrados en Tiempo Real (Consolidación Institucional 90 Alumnos)
    const unsubStudents = listenToUnifiedCollection("students", (cloudStudents) => {
      const localStudents = JSON.parse(localStorage.getItem('motor_pedagogico_students_db') || '[]');
      
      const allUnfiltered = [...cloudStudents, ...localStudents, ...INSTITUTIONAL_STUDENTS];
      const uniqueMap = new Map();
      allUnfiltered.forEach(item => {
        const codeKey = item.code || item.id || item.username;
        if (!uniqueMap.has(codeKey)) {
          uniqueMap.set(codeKey, item);
        }
      });

      setStudentsList(Array.from(uniqueMap.values()));
      setLastSyncTime(new Date().toLocaleTimeString('es-CO'));
    });

    return () => {
      unsubActivities();
      unsubStudents();
    };
  }, []);

  // Función auxiliar para normalizar nombres de grupo (ej: "602", "Grupo 602", "grupo 602")
  const matchesGroup = (itemGroup, selected) => {
    if (!selected || selected === 'all') return true;
    if (!itemGroup) return false;
    const cleanItem = itemGroup.toString().replace(/^grupo\s*/i, '').trim();
    const cleanSelected = selected.toString().replace(/^grupo\s*/i, '').trim();
    return cleanItem === cleanSelected || itemGroup.toString().toLowerCase().includes(cleanSelected.toLowerCase());
  };

  // Filtrado dinámico por grupo
  const filteredActivities = selectedGroup === 'all' 
    ? activitiesList 
    : activitiesList.filter(item => matchesGroup(item.group, selectedGroup));

  const filteredStudents = selectedGroup === 'all'
    ? studentsList
    : studentsList.filter(student => matchesGroup(student.grade || student.group, selectedGroup));

  const averagePct = filteredActivities.length > 0 
    ? Math.round(filteredActivities.reduce((acc, curr) => acc + (curr.pct || 85), 0) / filteredActivities.length)
    : 85;

  const totalXPClass = filteredActivities.reduce((acc, curr) => acc + (curr.xp || 100), 0);

  // Abrir/Cerrar Pantalla Completa del Navegador
  const toggleFullScreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen().catch(() => {});
    } else {
      if (document.exitFullscreen) {
        document.exitFullscreen().catch(() => {});
      }
    }
  };

  // Exportación real de calificaciones a archivo CSV / Excel
  const exportToCSV = () => {
    if (filteredActivities.length === 0) {
      alert("No hay actividades registradas en el grupo seleccionado para exportar.");
      return;
    }
    const headers = ["ID Estudiante", "Nombre Estudiante", "Grupo", "Tema", "Tipo Evaluacion", "Puntaje", "Porcentaje", "XP Ganados", "Fecha"];
    const rows = filteredActivities.map(act => [
      `"${act.id || ''}"`,
      `"${act.name || ''}"`,
      `"${act.group || ''}"`,
      `"${act.topic || ''}"`,
      `"${act.type || ''}"`,
      `"${act.score || ''}"`,
      `"${act.pct || 0}%"`,
      `"${act.xp || 0}"`,
      `"${act.date || ''}"`
    ]);

    const csvContent = "data:text/csv;charset=utf-8,\uFEFF" 
      + [headers.join(","), ...rows.map(r => r.join(","))].join("\n");
      
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", `Reporte_Notas_English_EOH_${selectedGroup.replace(/\s+/g, '_')}_${new Date().toISOString().slice(0, 10)}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Función para abrir vista de impresión limpia (Generar PDF)
  const handlePrintReport = () => {
    window.print();
  };

  return (
    <div className="space-y-6 text-slate-100">
      
      {/* 1. TARJETAS DE RESUMEN GLOBAL EN TIEMPO REAL */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 print:hidden">
        
        <div className="bg-white text-slate-800 p-5 rounded-2xl shadow-lg border border-slate-200 flex items-center gap-4">
          <div className="bg-blue-100 p-3.5 rounded-2xl text-2xl">👥</div>
          <div>
            <div className="text-[10px] font-black uppercase text-slate-500 tracking-wider">Estudiantes Registrados</div>
            <div className="text-3xl font-black text-slate-900">{studentsList.length}</div>
            <div className="text-xs font-semibold text-emerald-600">Sincronizados en tiempo real</div>
          </div>
        </div>

        <div className="bg-white text-slate-800 p-5 rounded-2xl shadow-lg border border-slate-200 flex items-center gap-4">
          <div className="bg-purple-100 p-3.5 rounded-2xl text-2xl">📝</div>
          <div>
            <div className="text-[10px] font-black uppercase text-slate-500 tracking-wider">Evaluaciones Presentadas</div>
            <div className="text-3xl font-black text-slate-900">{filteredActivities.length}</div>
            <div className="text-xs font-semibold text-purple-600">Registradas en la nube</div>
          </div>
        </div>

        <div className="bg-white text-slate-800 p-5 rounded-2xl shadow-lg border border-slate-200 flex items-center gap-4">
          <div className="bg-amber-100 p-3.5 rounded-2xl text-2xl">⭐</div>
          <div>
            <div className="text-[10px] font-black uppercase text-slate-500 tracking-wider">Promedio General</div>
            <div className="text-3xl font-black text-slate-900">{averagePct}%</div>
            <div className="text-xs font-semibold text-amber-600">Desempeño acumulado</div>
          </div>
        </div>

        <div className="bg-white text-slate-800 p-5 rounded-2xl shadow-lg border border-slate-200 flex items-center gap-4">
          <div className="bg-emerald-100 p-3.5 rounded-2xl text-2xl">🚀</div>
          <div>
            <div className="text-[10px] font-black uppercase text-slate-500 tracking-wider">Puntos XP Totales</div>
            <div className="text-3xl font-black text-slate-900">{totalXPClass} XP</div>
            <div className="text-xs font-semibold text-emerald-600">Acumulado del grupo</div>
          </div>
        </div>

      </div>

      {/* 2. ENCABEZADO PANEL MAESTRO Y BOTÓN MODO PROYECTOR ACTIVADO */}
      <div className="bg-slate-900/90 border border-slate-800 p-6 md:p-8 rounded-3xl shadow-2xl flex flex-wrap justify-between items-center gap-4 print:hidden">
        <div>
          <span className="text-[10px] font-bold text-blue-400 uppercase tracking-widest">Panel Maestro de Administración</span>
          <h2 className="text-2xl md:text-3xl font-extrabold text-white mt-1">Profesor Didier Antonio Alvarez Ramos</h2>
          <p className="text-xs text-slate-400 mt-1">Monitoreo en vivo desde cualquier celular o computador de la IE Enrique Olaya Herrera</p>
        </div>

        <div className="flex gap-2 flex-wrap">
          <button 
            onClick={() => {
              if ('caches' in window) {
                caches.keys().then(names => names.forEach(n => caches.delete(n)));
              }
              window.location.reload();
            }}
            className="bg-emerald-600 hover:bg-emerald-500 text-white font-bold px-4 py-2.5 rounded-2xl text-xs shadow-lg transition flex items-center gap-1.5 cursor-pointer"
          >
            🔄 Sincronizar Ahora
          </button>

          {/* 📺 BOTÓN ACTIVO MODO PROYECTOR DE SALÓN */}
          <button 
            onClick={() => {
              setShowProjectorMode(true);
              toggleFullScreen();
            }}
            className="bg-blue-600 hover:bg-blue-500 text-white font-bold px-5 py-2.5 rounded-2xl text-xs shadow-lg transition flex items-center gap-2 cursor-pointer transform hover:scale-105 border border-blue-400"
          >
            📺 Lanzar Modo Proyector de Salón 🚀
          </button>
        </div>
      </div>

      {/* 3. FILTRO DE GRUPOS Y EXPORTACIONES */}
      <div className="bg-slate-900/70 border border-slate-800 p-6 rounded-3xl shadow-xl space-y-4 print:hidden">
        <div className="flex flex-wrap justify-between items-center gap-4">
          <div className="space-y-1">
            <label className="text-xs font-bold text-slate-300 uppercase tracking-wider block mb-1">
              Seleccionar Grupo para Monitoreo:
            </label>
            <select 
              value={selectedGroup}
              onChange={(e) => setSelectedGroup(e.target.value)}
              className="bg-slate-950 border border-slate-700 text-white font-bold text-sm px-4 py-2.5 rounded-xl outline-none focus:border-blue-500 transition cursor-pointer"
            >
              <option value="all">🌐 Todos los Grupos ({studentsList.length} Estudiantes Inscritos)</option>
              <option value="Grupo 601">Grupo 601 ({studentsList.filter(s => matchesGroup(s.grade || s.group, '601')).length} alumnos)</option>
              <option value="Grupo 602">Grupo 602 ({studentsList.filter(s => matchesGroup(s.grade || s.group, '602')).length} alumnos)</option>
              <option value="Grupo 603">Grupo 603 ({studentsList.filter(s => matchesGroup(s.grade || s.group, '603')).length} alumnos)</option>
              <option value="Grupo 701">Grupo 701 ({studentsList.filter(s => matchesGroup(s.grade || s.group, '701')).length} alumnos)</option>
              <option value="Grupo 702">Grupo 702 ({studentsList.filter(s => matchesGroup(s.grade || s.group, '702')).length} alumnos)</option>
              <option value="Grupo 703">Grupo 703 ({studentsList.filter(s => matchesGroup(s.grade || s.group, '703')).length} alumnos)</option>
              <option value="Grupo 901">Grupo 901 ({studentsList.filter(s => matchesGroup(s.grade || s.group, '901')).length} alumnos)</option>
              <option value="Grupo 902">Grupo 902 ({studentsList.filter(s => matchesGroup(s.grade || s.group, '902')).length} alumnos)</option>
              <option value="Grupo 903">Grupo 903 ({studentsList.filter(s => matchesGroup(s.grade || s.group, '903')).length} alumnos)</option>
            </select>
          </div>

          <div className="flex items-center gap-2 flex-wrap">
            <button 
              onClick={exportToCSV}
              className="bg-emerald-600 hover:bg-emerald-500 text-white font-bold px-4 py-2 rounded-xl text-xs transition flex items-center gap-1.5 shadow cursor-pointer"
            >
              📊 Exportar Excel (.csv)
            </button>
            <button 
              onClick={handlePrintReport}
              className="bg-sky-600 hover:bg-sky-500 text-white font-bold px-4 py-2 rounded-xl text-xs transition flex items-center gap-1.5 shadow cursor-pointer"
            >
              📄 Imprimir / Guardar PDF
            </button>
          </div>
        </div>
      </div>

      {/* 4. PESTAÑAS DEL PANEL: REPORTES VS LISTA DE ESTUDIANTES */}
      <div className="bg-slate-900/90 border border-slate-800 rounded-3xl p-6 shadow-2xl space-y-4 print:bg-transparent print:border-none print:shadow-none print:p-0 print:m-0">
        
        {/* BOTONES DE PESTAÑAS */}
        <div className="flex gap-2 border-b border-slate-800 pb-3 print:hidden">
          <button
            onClick={() => setActiveDashboardTab('activities')}
            className={`px-4 py-2.5 rounded-xl font-bold text-xs transition cursor-pointer flex items-center gap-2 ${
              activeDashboardTab === 'activities' 
                ? 'bg-blue-600 text-white shadow-lg' 
                : 'bg-slate-800/80 text-slate-400 hover:text-white'
            }`}
          >
            📋 Reporte de Actividades y Notas ({filteredActivities.length})
          </button>

          <button
            onClick={() => setActiveDashboardTab('students')}
            className={`px-4 py-2.5 rounded-xl font-bold text-xs transition cursor-pointer flex items-center gap-2 ${
              activeDashboardTab === 'students' 
                ? 'bg-purple-600 text-white shadow-lg' 
                : 'bg-slate-800/80 text-slate-400 hover:text-white'
            }`}
          >
            👥 Lista de Alumnos Registrados ({filteredStudents.length})
          </button>

          <button
            onClick={() => setActiveDashboardTab('printable_pdf')}
            className={`px-4 py-2.5 rounded-xl font-bold text-xs transition cursor-pointer flex items-center gap-2 ${
              activeDashboardTab === 'printable_pdf' 
                ? 'bg-sky-600 text-white shadow-lg border border-sky-400' 
                : 'bg-sky-950/80 border border-sky-500/30 text-sky-200 hover:bg-sky-600 hover:text-white'
            }`}
          >
            🖨️ Pruebas Imprimibles PDF (Contingencia) 📄
          </button>

          <button
            onClick={() => setActiveDashboardTab('exam_pin')}
            className={`px-4 py-2.5 rounded-xl font-bold text-xs transition cursor-pointer flex items-center gap-2 ${
              activeDashboardTab === 'exam_pin' 
                ? 'bg-gradient-to-r from-purple-600 to-indigo-600 text-white shadow-lg border border-purple-400' 
                : 'bg-purple-950/80 border border-purple-500/30 text-purple-200 hover:bg-purple-600 hover:text-white'
            }`}
          >
            🔑 Generador de PIN de Evaluación (10 min) 🚀
          </button>

          <button
            onClick={() => setActiveDashboardTab('drive_worksheets')}
            className={`px-4 py-2.5 rounded-xl font-bold text-xs transition cursor-pointer flex items-center gap-2 ${
              activeDashboardTab === 'drive_worksheets' 
                ? 'bg-amber-600 text-white shadow-lg border border-amber-400' 
                : 'bg-amber-950/80 border border-amber-500/30 text-amber-200 hover:bg-amber-600 hover:text-white'
            }`}
          >
            📁 Guías Drive (PDF) 📄
          </button>
        </div>

        {/* CONTENIDO DE PESTAÑAS */}
        {activeDashboardTab === 'drive_worksheets' ? (
          <WorksheetRepository />
        ) : activeDashboardTab === 'printable_pdf' ? (
          <PrintableExamGenerator />
        ) : activeDashboardTab === 'exam_pin' ? (
          <TeacherExamPinManager />
        ) : activeDashboardTab === 'activities' ? (
          <div className="space-y-4">
            <div className="flex justify-between items-center pb-2">
              <h3 className="text-base font-bold text-white flex items-center gap-2">
                📋 Reporte de Actividades — {selectedGroup === 'all' ? `Todos los Grupos (${filteredActivities.length} registros)` : `${selectedGroup} (${filteredActivities.length} registros)`}
              </h3>
              <span className="text-[10px] font-bold bg-emerald-500/20 text-emerald-400 border border-emerald-500/30 px-3 py-1 rounded-full flex items-center gap-1">
                <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse"></span>
                Escuchando Firebase Cloud ({lastSyncTime})
              </span>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-left text-xs">
                <thead className="bg-slate-950 text-slate-400 font-bold uppercase tracking-wider border-b border-slate-800">
                  <tr>
                    <th className="p-3">#</th>
                    <th className="p-3">ID Estudiante</th>
                    <th className="p-3">Nombre Estudiante</th>
                    <th className="p-3">Grupo</th>
                    <th className="p-3">Unidad Temática</th>
                    <th className="p-3">Tipo Actividad</th>
                    <th className="p-3">Aciertos</th>
                    <th className="p-3">Puntaje (%)</th>
                    <th className="p-3">Puntos XP</th>
                    <th className="p-3">Fecha</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-800/60 text-slate-200">
                  {filteredActivities.map((s, idx) => (
                    <tr key={idx} className="hover:bg-slate-800/50 transition">
                      <td className="p-3 font-bold text-slate-500">{idx + 1}</td>
                      <td className="p-3 font-mono font-bold text-blue-400">{s.id}</td>
                      <td className="p-3 font-bold text-white">{s.name}</td>
                      <td className="p-3 font-semibold text-slate-400">{s.group}</td>
                      <td className="p-3">{s.topic}</td>
                      <td className="p-3">{s.type}</td>
                      <td className="p-3 font-semibold">{s.score}</td>
                      <td className="p-3 font-bold text-emerald-400">{s.pct}%</td>
                      <td className="p-3 font-bold text-blue-400">{s.xp} XP</td>
                      <td className="p-3 text-slate-400">{s.date}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ) : (
          /* TABLA 2: LISTA DE ALUMNOS REGISTRADOS */
          <div className="space-y-4">
            <div className="flex justify-between items-center pb-2">
              <h3 className="text-base font-bold text-white flex items-center gap-2">
                👥 Lista de Estudiantes Inscritos — {selectedGroup === 'all' ? `Todos los Grupos (${filteredStudents.length} alumnos)` : `${selectedGroup} (${filteredStudents.length} alumnos)`}
              </h3>
              <span className="text-[10px] font-bold bg-purple-500/20 text-purple-300 border border-purple-500/30 px-3 py-1 rounded-full">
                Sincronización en Vivo con Registro
              </span>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-left text-xs">
                <thead className="bg-slate-950 text-slate-400 font-bold uppercase tracking-wider border-b border-slate-800">
                  <tr>
                    <th className="p-3">#</th>
                    <th className="p-3">Código de Acceso EOH</th>
                    <th className="p-3">Nombre Completo del Alumno</th>
                    <th className="p-3">Grado / Grupo</th>
                    <th className="p-3">Fecha de Nacimiento</th>
                    <th className="p-3">Fecha de Registro</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-800/60 text-slate-200">
                  {filteredStudents.map((st, idx) => (
                    <tr key={idx} className="hover:bg-slate-800/50 transition">
                      <td className="p-3 font-bold text-slate-500">{idx + 1}</td>
                      <td className="p-3 font-mono font-bold text-emerald-400">{st.code || st.id || 'EOH-601-1234'}</td>
                      <td className="p-3 font-bold text-white">{st.username || st.name}</td>
                      <td className="p-3 font-semibold text-blue-400">{st.grade ? 'Grupo ' + st.grade : st.group || 'Grupo 601'}</td>
                      <td className="p-3 font-mono text-slate-300">{st.birthdate || '14/05/2013'}</td>
                      <td className="p-3 text-slate-400">{st.createdAt || st.date || 'Reciente'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

      </div>

      {/* 5. 📺 INTERFAZ MODAL VIRTUAL MODO PROYECTOR DE SALÓN (GRAN FORMATO) */}
      {showProjectorMode && (
        <div className="fixed inset-0 bg-slate-950 z-50 p-6 md:p-12 overflow-y-auto flex flex-col justify-between space-y-8 animate-fadeIn">
          
          {/* BARRA DE CONTROL DEL PROYECTOR */}
          <div className="flex justify-between items-center border-b border-slate-800 pb-6">
            <div className="flex items-center space-x-4">
              <span className="text-4xl">📺</span>
              <div>
                <h2 className="text-2xl md:text-4xl font-black text-white">MODO PROYECTOR — SALÓN DE CLASES IE EOH</h2>
                <p className="text-sm text-blue-400 font-bold">Monitoreo en Vivo • Lic. Didier Antonio Alvarez Ramos</p>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <button 
                onClick={toggleFullScreen}
                className="bg-slate-800 hover:bg-slate-700 text-white font-bold px-4 py-2.5 rounded-xl text-xs border border-slate-700 transition cursor-pointer"
              >
                🖥️ Pantalla Completa
              </button>
              
              <button 
                onClick={() => setShowProjectorMode(false)}
                className="bg-red-600 hover:bg-red-500 text-white font-extrabold px-6 py-2.5 rounded-xl text-xs transition shadow-lg cursor-pointer"
              >
                ✕ Salir de Modo Proyector
              </button>
            </div>
          </div>

          {/* MÉTRICAS DE GRAN FORMATO PARA TELEVISOR / VIDEOBEAM */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <div className="bg-blue-900/40 border-2 border-blue-500 p-6 rounded-3xl text-center space-y-2">
              <div className="text-sm font-extrabold uppercase text-blue-300">Alumnos Inscritos</div>
              <div className="text-6xl font-black text-white">{studentsList.length}</div>
            </div>

            <div className="bg-purple-900/40 border-2 border-purple-500 p-6 rounded-3xl text-center space-y-2">
              <div className="text-sm font-extrabold uppercase text-purple-300">Talleres Entregados</div>
              <div className="text-6xl font-black text-white">{filteredActivities.length}</div>
            </div>

            <div className="bg-amber-900/40 border-2 border-amber-500 p-6 rounded-3xl text-center space-y-2">
              <div className="text-sm font-extrabold uppercase text-amber-300">Puntos XP Salón</div>
              <div className="text-6xl font-black text-amber-300">{totalXPClass} XP</div>
            </div>

            <div className="bg-emerald-900/40 border-2 border-emerald-500 p-6 rounded-3xl text-center space-y-2">
              <div className="text-sm font-extrabold uppercase text-emerald-300">Promedio General</div>
              <div className="text-6xl font-black text-emerald-400">{averagePct}%</div>
            </div>
          </div>

          {/* TABLA EN FUENTE GIGANTE PARA PROYECCIÓN DE ENTREGA DE NOTAS */}
          <div className="bg-slate-900/90 border-2 border-slate-800 rounded-3xl p-6 md:p-8 space-y-4 shadow-2xl flex-1">
            <div className="flex justify-between items-center pb-4 border-b border-slate-800">
              <h3 className="text-xl md:text-2xl font-black text-white flex items-center gap-3">
                🏆 Tabla de Posiciones y Actividades en Vivo — {selectedGroup === 'all' ? 'Todos los Grupos' : selectedGroup}
              </h3>
              <span className="text-xs font-bold bg-emerald-500/20 text-emerald-400 border border-emerald-500/40 px-4 py-1.5 rounded-full">
                Sincronización en Tiempo Real ({lastSyncTime})
              </span>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm md:text-base">
                <thead className="bg-slate-950 text-slate-400 font-extrabold uppercase tracking-wider border-b border-slate-800">
                  <tr>
                    <th className="p-4">#</th>
                    <th className="p-4">Código EOH</th>
                    <th className="p-4">Estudiante</th>
                    <th className="p-4">Grupo</th>
                    <th className="p-4">Unidad</th>
                    <th className="p-4">Aciertos</th>
                    <th className="p-4">Puntaje</th>
                    <th className="p-4">Puntos XP</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-800/80 text-slate-100 font-semibold">
                  {filteredActivities.slice(0, 10).map((s, idx) => (
                    <tr key={idx} className="hover:bg-slate-800/60 transition">
                      <td className="p-4 font-black text-blue-400 text-lg">{idx + 1}</td>
                      <td className="p-4 font-mono font-bold text-emerald-400">{s.id}</td>
                      <td className="p-4 font-black text-white text-lg">{s.name}</td>
                      <td className="p-4 text-slate-300">{s.group}</td>
                      <td className="p-4">{s.topic}</td>
                      <td className="p-4 font-bold">{s.score}</td>
                      <td className="p-4 font-black text-emerald-400 text-lg">{s.pct}%</td>
                      <td className="p-4 font-black text-blue-400 text-lg">+{s.xp} XP 🌟</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="text-center text-xs text-slate-400">
            &copy; 2026 English EOH — Modo Proyector Oficial para la Institución Educativa Enrique Olaya Herrera
          </div>

        </div>
      )}

    </div>
  );
}